import type { ServerWebExchange } from '../../../types/web/server';
import type { WebHandler } from '../server/types.ts';
import type { CorsConfigSource, PreFlightRequestHandler } from '../cors/types.ts';
import { isPreFlightRequest } from '../cors/utils.ts';
import { HttpStatus } from '../../http/status.ts';
import type { HandlerAdapter, HandlerMapping, HandlerResult, HandlerResultHandler } from './types.ts';
import getLogger from '../../logger.ts';
import { ResponseStatusError } from '../server/error.ts';

export const BEST_MATCHING_HANDLER_ATTRIBUTE = 'io.interop.web.async.handler.handler';
export const BEST_MATCHING_PATTERN_ATTRIBUTE = 'io.interop.web.async.handler.pattern';
export const URI_VARIABLES_ATTRIBUTE         = 'io.interop.web.async.handler.variables';

export class DispatcherHandler implements WebHandler, PreFlightRequestHandler {
    #handlerMappings?: Array<HandlerMapping>;
    #handlerAdapters?: Array<HandlerAdapter>;
    #resultHandlers?: Array<HandlerResultHandler>;

    constructor(handlerMappings?: Array<HandlerMapping>,
                handlerAdapters?: Array<HandlerAdapter>,
                resultHandlers?: Array<HandlerResultHandler>) {
        this.#handlerMappings = handlerMappings;
        this.#handlerAdapters = handlerAdapters;
        this.#resultHandlers = resultHandlers;
    }
    async handle(exchange: ServerWebExchange): Promise<void> {
        if (!this.#handlerMappings) {
            return this.#createNotFoundError();
        }
        if (isPreFlightRequest(exchange.request)) {
            return this.handlePreFlight(exchange);
        }
        let handler: unknown;
        try {
            for (const mapping of this.#handlerMappings) {
                handler = await mapping.getHandler(exchange);
                if (handler !== undefined) {
                    break;
                }
            }
            if (handler === undefined) {
                await this.#createNotFoundError();
            }
        }
        catch (e) {
            return this.#handleResultPromise(exchange, Promise.reject(e));
        }
        return this.#handleRequestWith(exchange, handler);
    }

    async #handleResultPromise(exchange: ServerWebExchange, resultPromise: Promise<HandlerResult>): Promise<void> {
        if (this.#handlerAdapters) {
            // dispatch exception handler
        }
        const result = await resultPromise;
        try {
            await this.#handleResult(exchange, result, `Handler ${result.handler}`);
        }
        catch (e) {
            const exceptionHandler = result.exceptionHandler;
            if (exceptionHandler) {
                const error = e instanceof Error ? e : new Error(String(e));
                const result2 = await exceptionHandler(exchange, error);
                await this.#handleResult(exchange, result2, `Exception handler ${result2.handler}, error="${error.message}"`);
            }
        }
    }

    #handleResult(exchange: ServerWebExchange, result: HandlerResult, details: string): Promise<void> {
        if (this.#resultHandlers) {
            for (const handler of this.#resultHandlers) {
                if (handler.supports(result)) {
                    details += ' [DispatcherHandler]';
                    return handler.handle(exchange, result);
                }
            }
        }
        return Promise.reject(new Error(`No HandlerResultHandler for ${result.returnValue}`));
    }

    #handleRequestWith(exchange: ServerWebExchange, handler: unknown): Promise<void> {
        if (exchange.response.statusCode?.value === HttpStatus.FORBIDDEN.value) {
            return Promise.resolve(); // Pre-flight request was denied
        }
        if (this.#handlerAdapters) {
            for (const adapter of this.#handlerAdapters) {
                if (adapter.supports(handler)) {
                    const resultPromise = adapter.handle(exchange, handler);
                    return this.#handleResultPromise(exchange, resultPromise);
                }
            }
        }
        return Promise.reject(new Error(`No HandlerAdapter for handler ${handler}`));
    }


    #createNotFoundError<R>(): Promise<R> {
        return Promise.reject(new ResponseStatusError(HttpStatus.NOT_FOUND));
    }

    async handlePreFlight(exchange: ServerWebExchange): Promise<void> {
        let handler: unknown;
        for (const mapping of this.#handlerMappings ?? []) {
            handler = await mapping.getHandler(exchange);
            if (handler !== undefined) {
                break;
            }
        }
        // If no handler found, return 403 Forbidden
        if (handler === undefined) {
            exchange.response.setStatusCode(HttpStatus.FORBIDDEN);
            return;
        }
    }
}


export abstract class AbstractHandlerMapping implements HandlerMapping {
    protected logger = getLogger('web.async.handler');
    #corsConfigSource?: CorsConfigSource;

    set corsConfigSource(source: CorsConfigSource) {
        if (source === undefined) {
            throw new Error('CorsConfigSource must not be undefined');
        }
        this.#corsConfigSource = source;
    }

    async getHandler(exchange: ServerWebExchange): Promise<unknown | undefined> {
        const handler = await this.getHandlerInternal(exchange);
        if (handler !== undefined) {
            if (this.logger.enabledFor('debug')) {
                this.logger.debug(`${exchange.logPrefix}Mapped to ${handler}`);
            }
            const request = exchange.request;
            if (this.hasCorsConfigSource(handler) || isPreFlightRequest(request)) {
                const config = this.#corsConfigSource !== undefined ? await this.#corsConfigSource(exchange) : undefined;
                const handlerConfig = this.getCorsConfig(handler, exchange);
                // Merge configs if both exist
            }
            return handler;
        }
    }

    protected abstract getHandlerInternal(exchange: ServerWebExchange): Promise<unknown | undefined>;

    protected hasCorsConfigSource(handler: unknown): boolean {
        return this.#corsConfigSource !== undefined;
    }

    protected getCorsConfig(handler: unknown, exchange: ServerWebExchange) {
        return undefined;
    }
}
